import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Compass, Sparkles, X, Flame } from "lucide-react";
import api from "@/lib/api";

const POLL_MS = 30_000;       // poll every 30s while idle
const IDLE_AFTER_MS = 90_000; // only show after 90s of idle

/**
 * MagnetCard — proactive nudge for idle online drivers. Polls
 * /chauffeur/drivers/me/magnet at 30s cadence while idle, only renders
 * when there's a meaningful suggestion. Dismissible per session.
 *
 * Two stacked nudges in priority order:
 *   1. 🔥 Hot-zone spike  — a known popular pin firing >=3× its 7-day
 *                            hourly baseline in the last 60 min.
 *   2. ⚡  Open-request    — driver-density-biased compass nudge from the
 *                            last 10 min of fresh requests.
 *
 * Drivers may dismiss either independently per session.
 */
export default function MagnetCard({ enabled }) {
  const [suggestion, setSuggestion] = useState(null);
  const [dismissedKey, setDismissedKey] = useState(null);
  const [dismissedSpikeKey, setDismissedSpikeKey] = useState(null);
  const idleSinceRef = useRef(Date.now());

  useEffect(() => {
    if (enabled) {
      idleSinceRef.current = Date.now();
    } else {
      setSuggestion(null);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancel = false;
    const fetchOnce = async () => {
      if (Date.now() - idleSinceRef.current < IDLE_AFTER_MS) return;
      try {
        const res = await api.get("/chauffeur/drivers/me/magnet");
        if (cancel) return;
        setSuggestion(res.data || null);
      } catch { /* ignore */ }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, POLL_MS);
    return () => { cancel = true; clearInterval(t); };
  }, [enabled]);

  if (!enabled || !suggestion) return null;

  // Spike & open-request nudges are independent — either may render alone.
  const spike = suggestion.hot_zone || null;
  const spikeKey = spike ? `${spike.lat.toFixed(3)},${spike.lng.toFixed(3)}` : null;
  const showSpike = spike && spikeKey !== dismissedSpikeKey;

  const hasOpen = suggestion.lat != null && suggestion.lng != null && suggestion.open_requests_in_zone > 0;
  const openKey = hasOpen ? `${suggestion.lat.toFixed(3)},${suggestion.lng.toFixed(3)}` : null;
  const showOpen = hasOpen && openKey !== dismissedKey;

  if (!showSpike && !showOpen) return null;

  return (
    <AnimatePresence>
      {showSpike && (
        <motion.div
          key="spike"
          initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6 }}
          data-testid="magnet-spike-card"
          className="relative p-4 border border-amber-500/50 bg-gradient-to-br from-amber-500/20 via-amber-500/5 to-transparent rounded-md mb-3"
        >
          <button
            data-testid="magnet-spike-dismiss"
            onClick={() => setDismissedSpikeKey(spikeKey)}
            className="absolute top-2 right-2 text-white/40 hover:text-white"
          >
            <X size={14} />
          </button>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-amber-300 mb-1 inline-flex items-center gap-1">
            <Flame size={11} className="fill-amber-400/40" /> Hot-zone spike · last hour
          </p>
          <p className="text-white text-sm font-body leading-snug">
            <span className="text-amber-300 font-heading">{spike.requests_last_hour}× requests</span>{" "}
            at <span className="text-amber-200">{spike.label}</span>{" "}
            ({spike.distance_km} km away) — running{" "}
            <span className="text-amber-300 font-mono">{spike.spike_ratio}×</span>{" "}
            its normal rate.
          </p>
          <a
            data-testid="magnet-spike-route"
            href={`https://www.google.com/maps/dir/?api=1&destination=${spike.lat},${spike.lng}`}
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1 mt-2 text-[11px] text-amber-200 hover:text-amber-100 underline"
          >
            <Compass size={10} /> Route to it
          </a>
        </motion.div>
      )}
      {showOpen && (
        <motion.div
          key="open"
          initial={{ opacity: 0, y: -6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -6 }}
          data-testid="magnet-card"
          className="relative p-4 border border-gold/40 bg-gradient-to-br from-gold/15 via-gold/5 to-transparent rounded-md mb-4"
        >
          <button
            data-testid="magnet-dismiss"
            onClick={() => setDismissedKey(openKey)}
            className="absolute top-2 right-2 text-white/40 hover:text-white"
          >
            <X size={14} />
          </button>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-gold mb-1 inline-flex items-center gap-1">
            <Sparkles size={10} /> Magnet Mode · ARIA suggests
          </p>
          <p className="text-white text-sm font-body leading-snug">
            Move <span className="text-gold font-heading">{suggestion.distance_km} km {suggestion.compass}</span>
            {suggestion.zone_label && <> toward <span className="text-gold">{suggestion.zone_label}</span></>}
            {" "}— <span className="text-white/80">{suggestion.open_requests_in_zone} open requests</span> there in the last {suggestion.window_minutes} min.
          </p>
          <p className="text-white/50 text-[10px] font-body mt-2 inline-flex items-center gap-1">
            <Compass size={10} /> {suggestion.zone_label || suggestion.compass} · driver-density bias
          </p>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
